package com.macrofocus.eflux.components

import com.macrofocus.common.json.JsonObject
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.Property
import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.components.link.GoogleMapLink
import com.macrofocus.eflux.components.link.OpenInfraMapLink
import com.macrofocus.eflux.network.Link
import com.macrofocus.eflux.network.LinkField
import com.macrofocus.eflux.network.coordinate.Coordinate
import com.macrofocus.eflux.network.line.Line
import com.macrofocus.eflux.ui.data.CoordinateChip
import com.macrofocus.eflux.ui.data.SubstationChip
import emotion.react.css
import js.objects.unsafeJso
import kotlinx.browser.window
import mui.material.*
import mui.material.Size
import mui.system.responsive
import mui.system.sx
import org.locationtech.jts.geom.GeometryFactory
import org.locationtech.jts.geom.Point
import org.locationtech.jts.io.WKTReader
import org.mkui.react.useProperty
import org.molap.dataframe.DataFrame
import org.molap.geojson.readGeoJson
import react.*
import react.dom.html.ReactHTML
import web.cssom.*

external interface LinkInfoExtraProps : PropsWithChildren {
    var primary: Boolean
    var dataLoader: DataLoader
    var link: Link
    var line: Line
    var tooltipLinks: Property<Array<LinkField<*>>>
    var masterDateTime: String?
    var expertMode: Boolean
    var tab: MutableProperty<String>
}

val LinkInfoExtra = FC<LinkInfoExtraProps> { props ->
    val dataLoader = props.dataLoader
    val path = props.link
    val line = props.line
    val masterDateTime = props.masterDateTime
    val expertMode = props.expertMode

    val (getCoordinate, setCoordinate) = useState<List<Coordinate>>()

    val (tab, setTab) = useProperty(props.tab)

    useEffect(masterDateTime, line.logicalBranchId) {
        window.fetch("/data/coordinates/${line.logicalBranchId}/$masterDateTime").then {
            it.json().then { it ->
                val dataFrame = DataFrame.readGeoJson(it.unsafeCast<JsonObject>())
                val list = List<Coordinate>(dataFrame.rowCount) { i ->
                    val logicalGeometry = if(dataFrame.columnIndex.contains("logicalGeometry")) {
                        val value = dataFrame.getValueAt(i, "logicalGeometry") as? String
                        if(value != null) {
                            WKTReader(GeometryFactory()).read(value)
                        } else {
                            null
                        }
                    } else {
                        null
                    }

                    Coordinate(dataFrame.getValueAt(i,"coordinateId") as? String,
                        dataFrame.getGeometry(i) as? Point,
                        logicalGeometry as? Point,
                        if(dataFrame.columnIndex.contains("seq")) dataFrame.getValueAt(i, "seq") as? Int else null,
                        if(dataFrame.columnIndex.contains("probability")) dataFrame.getValueAt(i, "probability") as? Double else null,
                        if(dataFrame.columnIndex.contains("risk_map")) (dataFrame.getValueAt(i, "risk_map") as? Number)?.toInt() else null,
                        if(dataFrame.columnIndex.contains("risk_list"))dataFrame.getValueAt(i, "risk_list") as? String else null,
                        dataFrame.getValueAt(i, "logicalBranchIds") as? String,
                        dataFrame.getValueAt(i, "description") as? String,
                        dataFrame.getValueAt(i, "t_70m") as? Double,
                        dataFrame.getValueAt(i, "wind_speed_70m") as? Double,
                        dataFrame.getValueAt(i, "wind_gusts_70m_1h") as? Double,
                        dataFrame.getValueAt(i, "precip_5min") as? Double,
                        dataFrame.getValueAt(i, "precip_type_intensity_5min") as? Double,
                        dataFrame.getValueAt(i, "forest_fire_warning") as? Double,
                        dataFrame.getValueAt(i, "power_line_oscillation_24h") as? Double,
                        dataFrame.getValueAt(i, "weather_code_1h") as? Double,
                        dataFrame.getValueAt(i, "tstorm_warning_1h") as? Double,
                        dataFrame.getValueAt(i, "air_density_70m") as? Double,
                        dataFrame.getValueAt(i, "count") as? Double)
//                }.sortedByDescending { it.probability ?: -1.0 }
                }.sortedBy { it.seq ?: -1 }
                setCoordinate(list)
            }
        }
    }

    ReactHTML.div {
        css {
            height = 10.px
        }
    }

    Tabs {
        sx {
            maxWidth = 270.px
        }

        this.value = tab
//                        variant = TabsVariant.scrollable
        asDynamic().size = Size.small
        onChange = { _, newValue -> setTab(newValue.toString()) }

        Tab {
            sx {
                textTransform = None.none
            }
            label = ReactNode("Assets")
            this.value = "assets"
        }

        Tab {
            sx {
                textTransform = None.none
            }
            label = ReactNode("Weather")
            this.value = "weather"
        }

        Tab {
            sx {
                textTransform = None.none
            }
            label = ReactNode("Links")
            this.value = "links"
        }
    }

    when (tab) {
        "assets" -> {
            Stack {
                direction = responsive(StackDirection.row)
                asDynamic().justifyContent = JustifyContent.center
                sx {
                    marginTop = 10.px
                    marginBottom = 10.px
                }
                SubstationChip {
                    this.dataLoader = dataLoader
                    this.primary = props.primary
                    this.probing = if(props.primary) dataLoader.controller.secondaryLogicalBusProbing else dataLoader.controller.primaryLogicalBusProbing
                    this.selection = if(props.primary) dataLoader.controller.secondaryLogicalBusSelection else dataLoader.controller.primaryLogicalBusSelection
                    this.logicalBusId = path.id.from
                    this.placement = TooltipPlacement.left
                }
                Typography {
                    +"-"
                }
                SubstationChip {
                    this.dataLoader = dataLoader
                    this.primary = props.primary
                    this.probing = if(props.primary) dataLoader.controller.secondaryLogicalBusProbing else dataLoader.controller.primaryLogicalBusProbing
                    this.selection = if(props.primary) dataLoader.controller.secondaryLogicalBusSelection else dataLoader.controller.primaryLogicalBusSelection
                    this.logicalBusId = path.id.to
                    this.placement = TooltipPlacement.right
                }
            }

            if(getCoordinate != null) {
                val totalChips = getCoordinate.size // adjust as needed
                val chipsPerRow = 10
                Box {
                    sx {
                        display = Display.flex
                        flexDirection = FlexDirection.column
                        //                    gap = 1

                        maxHeight = 25.vh
                        overflowY = Auto.auto
                    }
                    // Create rows
                    for (rowStart in 1..totalChips step chipsPerRow) {
                        val rowEnd = (rowStart + chipsPerRow - 1).coerceAtMost(totalChips)

                        Box {
                            sx {
                                display = Display.flex
                                flexWrap = FlexWrap.nowrap
//                                gap = 1
                            }
                            //                            direction = responsive(StackDirection.column)
                            //                            asDynamic().justifyContent = JustifyContent.center


                            // Create chips in row
                            for (number in rowStart..rowEnd) {
                                val coordinate = getCoordinate[number - 1]
                                CoordinateChip {
                                    this.dataLoader = dataLoader
                                    this.primary = props.primary
                                    this.probing = if(props.primary) dataLoader.controller.secondaryCoordinateProbing else dataLoader.controller.primaryCoordinateProbing
                                    this.selection = if(props.primary) dataLoader.controller.secondaryCoordinateSelection else dataLoader.controller.primaryCoordinateSelection
                                    this.coordinate = coordinate
                                }
                            }
                        }
                    }
                }
            }
        }

        "weather" -> {
            val marks = arrayOf(
                unsafeJso<dynamic> {
                    value = -50
                    label = "-50°C"
                },
                unsafeJso<dynamic> {
                    value = 0
                    label = "0°C"
                },
//                                unsafeJso<dynamic> {
//                                        value = 20
//                                        label = "20°C"
//                                },
//                                unsafeJso<dynamic> {
//                                        value = 37
//                                        label = "37°C"
//                                },
                unsafeJso<dynamic> {
                    value = 50
                    label = "50°C"
                },
            )

            Stack {
                direction = responsive(StackDirection.column)

                Typography {
                    +"Temperature range"
                }

                Slider {
                    sx {
                        maxWidth = 270.px
                    }
                    this.marks = marks
                    min = -50.0
                    max = 50.0
//                                    getAriaLabel="Temperature range"
                    defaultValue = arrayOf(line.mint70m, line.maxt70m)
                    valueLabelDisplay = "auto"
                }
            }

            Stack {
                direction = responsive(StackDirection.column)

                Typography {
                    +"Wind speed"
                }

                Slider {
                    sx {
                        maxWidth = 270.px
                    }
//                                    this.marks = marks
                    min = 0.0
                    max = 250.0
                    this.marks = arrayOf(
                        unsafeJso<dynamic> {
                            this.value = 0
                            this.label = "0"
                        },
                        unsafeJso<dynamic> {
                            this.value = 250
                            this.label = "250km/h"
                        },
                    )
                    //                                    getAriaLabel="Temperature range"
                    defaultValue = arrayOf(line.wind_speed_70m_min, line.wind_gusts_70m_1h_max)
                    valueLabelDisplay = "auto"
                }
            }

            Stack {
                direction = responsive(StackDirection.column)

                Typography {
                    +"Wind gust"
                }

                Slider {
                    sx {
                        maxWidth = 270.px
                    }
//                                    this.marks = marks
                    min = 0.0
                    max = 250.0
                    this.marks = arrayOf(
                        unsafeJso<dynamic> {
                            this.value = 0
                            this.label = "0"
                        },
                        unsafeJso<dynamic> {
                            this.value = 250
                            this.label = "250km/h"
                        },
                    )
                    //                                    getAriaLabel="Temperature range"
                    defaultValue = arrayOf(line.wind_gusts_70m_1h_min, line.wind_gusts_70m_1h_max)
                    valueLabelDisplay = "auto"
                }
            }

            Stack {
                direction = responsive(StackDirection.column)

                Typography {
                    +"Precipitation"
                }

                Slider {
                    sx {
                        maxWidth = 270.px
                    }
                    this.marks = arrayOf(
                        unsafeJso<dynamic> {
                            this.value = 0
                            this.label = "0"
                        },
                        unsafeJso<dynamic> {
                            this.value = 250
                            this.label = "10mm/5min"
                        },
                    )
                    min = 0.0
                    max = 10.0
                    //                                    getAriaLabel="Temperature range"
                    defaultValue = arrayOf(line.precip_5min_min, line.precip_5min_max)
                    valueLabelDisplay = "auto"
                }
            }
        }

        "links" -> {
            Box {
                sx {
                    display = Display.flex
                    flexDirection = FlexDirection.column
                    overflowY = Auto.auto
                }

                InstantZoomButton {
                    this.dataLoader = dataLoader
                    geometry = line.physicalGeometry
                    margin = 0.05
                }

                if (expertMode) {
                    AnimatedZoomButton {
                        this.dataLoader = dataLoader
                        geometry = line.physicalGeometry
                        margin = 0.05
                    }
                }

                GoogleMapLink {
                    geometry = line.physicalGeometry
                    margin = 0.05
                }

                OpenInfraMapLink {
                    geometry = line.physicalGeometry
                    margin = 0.05
                }
            }
        }
    }
}

